/**
 * Pure geometry for the board's overlay bands (`projects/terrain-tile-art.md` 5.1, ruling 6): a set
 * of cells drawn as one zone with a cell-exact outline and softened corners, the way the Phase 1
 * prototype drew move range, attack range and zones over the textured ground. No Pixi here;
 * `pixiCombatBoard.ts` strokes and fills what this returns.
 *
 * The outline is the prototype's: the cell mask blurred by a small Gaussian and contoured at 0.5
 * (`terrainArt/geometry.ts`), so it contains exactly its own cells' centres and rounds each corner by
 * a few px. Loops are then smoothed with quadratic curves through edge midpoints and flattened to
 * points, so a fill, a solid stroke and a dashed stroke all follow the same line.
 */
import { CELL, parseTileKey } from "./combatCells";
import { contours, coverageField, emptyMask, insideLoops, type CellMask, type Point } from "./terrainArt/geometry";

/** Gaussian sigma of the outline, in px: the corner rounding. The prototype's value. */
const BAND_SIGMA = 4;
/** Points per quadratic when flattening a smoothed loop. */
const CURVE_STEPS = 3;

/**
 * The on-board cells of `keys` as a mask padded by one empty cell on every side, with their bounding
 * rect in padded coordinates; `null` when none are on the board. The padding keeps a band that
 * touches the board edge closed at the edge: the terrain field clamps off-board reads to the nearest
 * board cell, which would carry the outline a cell past it.
 */
function paddedMask(keys: Iterable<string>, board: { cols: number; rows: number }): { mask: CellMask; x0: number; y0: number; x1: number; y1: number } | null {
  const width = board.cols + 2;
  const mask = emptyMask({ width, height: board.rows + 2 });
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const key of keys) {
    const { x, y } = parseTileKey(key);
    if (x < 0 || x >= board.cols || y < 0 || y >= board.rows) continue;
    mask.cells[(y + 1) * width + x + 1] = 1;
    x0 = Math.min(x0, x + 1);
    y0 = Math.min(y0, y + 1);
    x1 = Math.max(x1, x + 2);
    y1 = Math.max(y1, y + 2);
  }
  return x1 < 0 ? null : { mask, x0, y0, x1, y1 };
}

/** Smooth a closed loop with quadratics through its edge midpoints, flattened to points. */
function smoothLoop(L: Point[]): Point[] {
  const n = L.length;
  const mid = (k: number): Point => {
    const a = L[k % n]!;
    const b = L[(k + 1) % n]!;
    return [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
  };
  const out: Point[] = [];
  let from = mid(0);
  for (let k = 1; k <= n; k++) {
    const c = L[k % n]!;
    const to = mid(k);
    for (let s = 0; s < CURVE_STEPS; s++) {
      const t = s / CURVE_STEPS;
      const u = 1 - t;
      out.push([u * u * from[0] + 2 * u * t * c[0] + t * t * to[0], u * u * from[1] + 2 * u * t * c[1] + t * t * to[1]]);
    }
    from = to;
  }
  return out;
}

/** One filled piece of a band: an outer loop and the holes directly inside it. */
export type BandPiece = { outer: Point[]; holes: Point[][] };
export type BandGeometry = { loops: Point[][]; pieces: BandPiece[] };

/**
 * The band outline of `keys` (cell keys `"x,y"`): every smoothed loop, for stroking, and the loops
 * grouped as outer-plus-holes, for filling. A loop nested inside an even number of others is an
 * outer; one inside an odd number is a hole of the innermost outer around it. Off-board keys are
 * skipped; an empty or all-off-board set gives no loops.
 */
export function bandGeometry(keys: Iterable<string>, board: { cols: number; rows: number }): BandGeometry {
  const m = paddedMask(keys, board);
  if (!m) return { loops: [], pieces: [] };
  const F = coverageField(m.mask, { rect: { x0: m.x0, y0: m.y0, x1: m.x1, y1: m.y1 }, haloPx: CELL }, BAND_SIGMA);
  const loops = contours(F, 0.5).map((L) => smoothLoop(L.map(([x, y]): Point => [x - CELL, y - CELL])));
  const depth = loops.map((L, i) => loops.reduce((d, O, j) => (j !== i && insideLoops([O], L[0]![0], L[0]![1]) ? d + 1 : d), 0));
  const pieces: BandPiece[] = [];
  const pieceOf = new Map<number, BandPiece>();
  loops.forEach((L, i) => {
    if (depth[i]! % 2 === 0) {
      const piece = { outer: L, holes: [] };
      pieces.push(piece);
      pieceOf.set(i, piece);
    }
  });
  loops.forEach((L, i) => {
    if (depth[i]! % 2 === 0) return;
    const owner = loops.findIndex((O, j) => depth[j] === depth[i]! - 1 && insideLoops([O], L[0]![0], L[0]![1]));
    pieceOf.get(owner)?.holes.push(L);
  });
  return { loops, pieces };
}

/**
 * The drawn runs of a closed loop dashed `dash` on, `gap` off, measured continuously around the
 * loop so the pattern never restarts at a vertex. Each run is an open polyline.
 */
export function dashLoop(L: Point[], dash: number, gap: number): Point[][] {
  const runs: Point[][] = [];
  let run: Point[] | null = [L[0]!];
  let left = dash;
  let on = true;
  for (let k = 0; k < L.length; k++) {
    let [ax, ay] = L[k]!;
    const [bx, by] = L[(k + 1) % L.length]!;
    let len = Math.hypot(bx - ax, by - ay);
    while (len > 0) {
      const step = Math.min(left, len);
      const f = step / len;
      ax += (bx - ax) * f;
      ay += (by - ay) * f;
      len -= step;
      left -= step;
      if (on) run!.push([ax, ay]);
      if (left <= 1e-9) {
        if (on) runs.push(run!);
        on = !on;
        left = on ? dash : gap;
        run = on ? [[ax, ay]] : null;
      }
    }
  }
  if (on && run && run.length > 1) runs.push(run);
  return runs;
}

/** The centre of each on-board cell of `keys`, in px. */
export function cellCentres(keys: Iterable<string>, board: { cols: number; rows: number }): Point[] {
  const out: Point[] = [];
  for (const key of keys) {
    const { x, y } = parseTileKey(key);
    if (x < 0 || x >= board.cols || y < 0 || y >= board.rows) continue;
    out.push([x * CELL + CELL / 2, y * CELL + CELL / 2]);
  }
  return out;
}
